import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EditSalaryGenerateForm from "./components/EditSalaryGenerateForm";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { GetById, Update } from "adminApi/SalaryGenerateApi/SalaryGenerate";
import Spinner from "Common/Globle/Spinner";
import { SalaryGenerateRules } from "validation/ValidationRules";
import { validateForm } from "utils/validation";
import fields from "../json/SalaryGenerate.json";

/**
 * EditSalaryGenerate
 * 
 * Standard Code Flow for Edit Components
 */
const EditSalaryGenerate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { formdata } = fields;

    // Initialize form values dynamically based on json schema
    const initialValues = formdata.reduce((acc, field) => {
        acc[field.name] = "";
        return acc;
    }, {});

    const { values, handleChange, setValues, errors, setErrors } = useFormChange(initialValues, SalaryGenerateRules);
    const [fetchLoading, setFetchLoading] = useState(true);

    const [originalDates, setOriginalDates] = useState({ from: null, to: null });

    // Fetch existing salary generate record by ID
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            setFetchLoading(true);
            try {
                const res = await GetById(id);
                if (res?.data?.success && res.data.data) {
                    const data = res.data.data;
                    const fromStr = data.start_date ? new Date(data.start_date).toISOString().split('T')[0] : "";
                    const toStr = data.end_date ? new Date(data.end_date).toISOString().split('T')[0] : "";

                    setOriginalDates({ from: fromStr, to: toStr });

                    // Map backend data to form fields
                    setValues({
                        pay_frequency: data.pay_frequency?.toLowerCase() || "monthly",
                        salary_name: data.salary_name || "",
                        from: fromStr,
                        to: toStr,
                        status: data.status || "generated"
                    });
                }
            } catch (err) {
                console.error("EditSalaryGenerate fetch error:", err);
            } finally {
                setFetchLoading(false);
            }
        };
        fetchData();
    }, [id, setValues]);

    const { loading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            return await Update({ id, data: formValues });
        },
        (vals) => {
            // Client-side validation before submission
            const err = validateForm(vals, SalaryGenerateRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Salary Batch updated successfully"
    );

    // Customize fields based on user request:
    // 1. Pay Frequency disable
    // 2. From Date: disable before original date
    // 3. To Date: disable after original date
    const modifiedFields = {
        ...fields,
        formdata: fields.formdata.map(field => {
            if (field.name === "pay_frequency") {
                return { ...field, disabled: true };
            }
            if (field.name === "from") {
                return { 
                    ...field, 
                    minDate: originalDates.from,
                    maxDate: values.to || originalDates.to
                };
            }
            if (field.name === "to") {
                return { 
                    ...field, 
                    minDate: values.from || originalDates.from,
                    maxDate: originalDates.to
                };
            }
            return field;
        })
    };

    // Dropdown options configuration
    const optionConfig = {
        pay_frequency: {
            list: [
                { id: "monthly", name: "Monthly" },
                { id: "weekly", name: "Weekly" },
                { id: "daily", name: "Daily" }
            ],
            valueKey: "id",
            labelKey: "name"
        },
        status: {
            list: [
                { id: "generated", name: "Generated" },
                { id: "approved", name: "Approved" },
                { id: "paid", name: "Paid" },
                { id: "cancelled", name: "Cancelled" }
            ],
            valueKey: "id",
            labelKey: "name"
        }
    };

    if (fetchLoading) return <Spinner isSpinner={true} />;

    return (
        <EditSalaryGenerateForm
            fields={modifiedFields}
            values={values}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={(e) => handleSubmit(e, values)}
            loading={loading}
            optionConfig={optionConfig}
            title="Edit Salary Generate"
        />
    );
};

export default EditSalaryGenerate;
